//
// Copyright (c) 2025 Comcast
//

import { generatePath, matchPath } from 'react-router'
import { stringifyQuery } from 'utils/strings'
import { decamelizeKeys } from 'humps'

function generatePathSafely (pattern, paramsMap) {
  let path
  try {
    path = generatePath(pattern, paramsMap)
  } catch (e) {
    console.error('generatePath() error:', e)
  }
  return path
}

/**
  Example:
    const RouteAdminPartners = makeRoute('/admin/partners/:partnerId?')

    console.log(RouteAdminPartners.pattern)
    > '/admin/partners/:partnerId?'

    console.log(RouteAdminPartners({ partnerId: 123 }))
    > '/admin/partners/123'

    console.log(RouteAdminPartners.toUrl({ partnerId: 123 }))
    > '/admin/partners/123'

    console.log(RouteAdminPartners.toUrl())
    > '/admin/partners'

    console.log(RouteAdminPartners.matchUrlExact('/admin/partners'))
    > true
 */
function makeRoute (pattern) {
  const routeHandler = {
    get: function (target, prop) {
      if (prop === 'pattern') {
        return target()
      } else if (prop === 'toUrl') {
        return function (paramsMap) {
          return generatePathSafely(pattern, arguments[0])
        }
      } else if (prop === 'matchUrlExact' || prop === 'matchUrl') {
        return (url) => {
          return !!(matchPath({
            path: pattern,
            exact: (prop === 'matchUrlExact'),
          }, url))
        }
      }
    },

    apply: function (target, thisArg, argumentsList) {
      return generatePathSafely(pattern, argumentsList[0])
    },
  }
  return new Proxy(() => pattern, routeHandler)
}

// Routes with '*' are not supposed to be instantiated and don't have endpoints.
// They should be used just as a pattern to match multiple routes.

// React router v6 not support regexp in path, route validation should be moved to seperate component
// https://reactrouter.com/en/main/upgrading/v5#note-on-route-path-patterns

/* eslint-disable no-multi-spaces */
export const RouteTicketingSystems =                  makeRoute('/ticketing_systems')
export const RouteTicketingSystemsTickets =           makeRoute('/ticketing_systems/:ticketingSystemDisplayId/tickets/*')
export const RouteTicketingSystemsTicketNew =         makeRoute('/ticketing_systems/:ticketingSystemDisplayId/tickets/new/:ticketId?')
export const RouteTicketingSystemsTicketEdit =        makeRoute('/ticketing_systems/:ticketingSystemDisplayId/tickets/:ticketId/edit')
export const RouteTicketingSystemsTicketClone =       makeRoute('/ticketing_systems/:ticketingSystemDisplayId/tickets/:ticketId/clone')
export const RouteTicketingSystemsTicketAppRequests = makeRoute('/ticketing_systems/:ticketingSystemDisplayId/tickets/:ticketId/app_requests')
export const RouteTicketingSystemsTicket =            makeRoute('/ticketing_systems/:ticketingSystemDisplayId/tickets/:ticketId')
export const RouteTicketsSearchResults =              makeRoute('/tickets_search')

export const RouteTicketProviders       = makeRoute('/ticket_providers')
export const RouteTicketProviderTickets = makeRoute('/ticket_providers/:ticketProviderId/tickets')
export const RouteTicketProviderTicket  = makeRoute('/ticket_providers/:ticketProviderId/tickets/:ticketId')
export const RouteTicketProviderTicketsSearchResults = makeRoute('/ticket_providers/tickets_search')

export const RouteProfile =        makeRoute('/profile')
export const RouteSubscriptionsTickets =  makeRoute('/subscriptions/ticketing_systems/:ticketingSystemDisplayId/tickets')
export const RouteSubscriptionsViews =  makeRoute('/subscriptions/ticketing_systems/:ticketingSystemDisplayId/views')
export const RouteApiDoc =         makeRoute('/api_doc')
export const RouteApiDocWithPartnerName = makeRoute('/api_doc/:partnerName')
export const RouteError500Show =   makeRoute('/show_error_500')
export const RouteError500Test =   makeRoute('/test_error_500')
export const RouteErrorUnhandledTest = makeRoute('/test_unhandled_error')
export const RouteSandboxSession = makeRoute('/sandbox')
export const RouteSatClientsPage = makeRoute('/sat_clients')
export const RouteSatSecretsPage = makeRoute('/sat_clients/:satClientId/secrets')

export const RouteReleaseNotesAny =  makeRoute('/release_notes/*')
export const RouteReleaseNotes =     makeRoute('/release_notes/:releaseNoteId?')
export const RouteReleaseNotesAdd =  makeRoute('/release_notes/add')
export const RouteReleaseNotesEdit = makeRoute('/release_notes/:releaseNoteId/edit')

export const RouteAdminPartners                       = makeRoute('/admin/partners/:partnerId?')
export const RouteAdminProviderCredentials            = makeRoute('/admin/provider_credentials/:providerCredentialId?')
export const RouteAdminPartnersTicketingSystems       = makeRoute('/admin/partners/:partnerId/ticketing_systems/:ticketingSystemDisplayId?')
export const RouteAdminPartnersTicketingSystemColumns = makeRoute('/admin/partners/:partnerId/ticketing_systems/:ticketingSystemDisplayId/columns/:columnId?')
export const RouteAdminPartnersOauthAppsPage          = makeRoute('/admin/partners/:partnerId/oauth_apps')

export const RouteAdminSatClientsPage = makeRoute('/admin/sat_clients/:satClientId?')
export const RouteAdminSatSecretsPage = makeRoute('/admin/sat_clients/:satClientId/secrets')

export const RouteAdminSharedFieldMaps =       makeRoute('/admin/shared_fields_maps/:sharedFieldsMapId?')
export const RouteAdminSharedFieldMapColumns = makeRoute('/admin/shared_fields_maps/:sharedFieldsMapId/columns/:columnId?')

export const RouteAdminProviders       = makeRoute('/admin/ticket_providers/:ticketProviderId?')
export const RouteAdminProviderQueries = makeRoute('/admin/ticket_providers/:ticketProviderId/queries/:queryId?')
export const RouteAdminProviderTicketBoards = makeRoute('/admin/ticket_providers/:ticketProviderId/provider_ticket_boards/:providerTicketBoardId?')
export const RouteAdminProviderTicketBoardTicketTableEditPage = makeRoute('/admin/ticket_providers/:ticketProviderId/provider_ticket_boards/:providerTicketBoardId/edit_ticket_table')
export const RouteAdminProviderTicketBoardCsvExportTableEditPage = makeRoute('/admin/ticket_providers/:ticketProviderId/provider_ticket_boards/:providerTicketBoardId/edit_csv_export_table')

export const RouteAdminStatusCollections = makeRoute('/admin/status_collections/:statusCollectionId?')
export const RouteAdminStatusCollection  = makeRoute('/admin/status_collections/:statusCollectionId/statuses/:partnerStatusId?')
export const RouteAdminStatusCollectionExternalStatuses = makeRoute('/admin/status_collections/:statusCollectionId/statuses/:partnerStatusId/external_statuses/:externalStatusId?')

export const RouteAdminMimeTypeCollections = makeRoute('/admin/mime_type_collections/:mimeTypeCollectionId?')
export const RouteAdminMimeTypeCollection  = makeRoute('/admin/mime_type_collections/:mimeTypeCollectionId?/mimetypes/:mimeTypeId?')

export const RouteAppRequests           = makeRoute('/admin/app_requests')

export const RouteAdminAuditHistoryAny =   makeRoute('/admin/audit_history/*')
export const RouteAdminAuditHistory =      makeRoute('/admin/audit_history/:type')
export const RouteAdminEmailHistory =      makeRoute('/admin/email_history')
export const RouteAdminUsers =             makeRoute('/admin/users')
export const RouteAdminUserFilters =       makeRoute('/admin/user_filters')
export const RouteAdminSubscriptionsTickets =  makeRoute('/subscriptions/ticket_providers/:ticketProviderId/tickets')
export const RouteAdminSubscriptionsViews =  makeRoute('/subscriptions/ticket_providers/:ticketProviderId/views')
export const RouteAdminFeedback =          makeRoute('/admin/feedback')
export const RouteAdminWebhookEvents =     makeRoute('/admin/webhook_events')

export const RouteAdminUsersExportCSV = (params) => {
  const query = stringifyQuery(decamelizeKeys(params))
  return (
    process.env.API_ENDPOINT_UI +
    '/admin/users_to_csv?' +
    `${query}`
  )
}

export const RouteAdminTicketingSystemExport = (partnerId, ticketingSystemDisplayId) => {
  return (
    `${process.env.API_ENDPOINT_UI}/admin/partners/${partnerId}/ticketing_systems/${ticketingSystemDisplayId}/export`
  )
}

export const RouteAdminStatusCollectionExport = (id) => {
  return (
    `${process.env.API_ENDPOINT_UI}/admin/status_collections/${id}/export`
  )
}

export const RouteAdminSharedFieldsMapExport = (sharedFieldsMapId) => {
  return (
    `${process.env.API_ENDPOINT_UI}/admin/shared_fields_maps/${sharedFieldsMapId}/export`
  )
}

export const RouteAdminMimeTypeCollectionExport = (mimeTypeCollectionId) => {
  return (
      `${process.env.API_ENDPOINT_UI}/admin/mime_type_collections/${mimeTypeCollectionId}/export`
  )
}

export const RouteAdminTicketProviderExport = (ticketProviderId) => {
  return (
      `${process.env.API_ENDPOINT_UI}/admin/ticket_providers/${ticketProviderId}/export`
  )
}

export const RouteAdminPartnerDoc = (partnerId) => {
  return (
    `${process.env.API_ENDPOINT_UI}/admin/partners/${partnerId}/api_doc_gen`
  )
}

export const RouteUserPartnerDoc = () => {
  return (
      `${process.env.API_ENDPOINT_UI}/api_doc`
  )
}

export const RouteFeatureFlags = () => {
  return (
    `${process.env.API_ENDPOINT}/flipper`
  )
}

export const RouteAsyncJobs = () => {
  return (
    `${process.env.API_ENDPOINT}/async_jobs`
  )
}

export const RouteMaintenanceTasks = () => {
  return (
    `${process.env.API_ENDPOINT}/maintenance_tasks`
  )
}

export const RouteDatabasePerformance = () => {
  return (
    `${process.env.API_ENDPOINT}/pg_extras`
  )
}

// generates /admin/audit_history/admin_actions/types[]=<entityName>&searchParam=auditable_id&search=<entityId>
export const RouteAdminAuditHistoryWithEntity = (entityName, entityId) => {
  const params = {
    types: [entityName],
    searchParam: 'auditable_id',
    searchValue: parseInt(entityId),
  }
  const search = stringifyQuery(params)
  return RouteAdminAuditHistory({ type: 'admin_actions' }) + `?${search}`
}

export const matchRoutesExact = (url, routes) => {
  return routes.some(route => route.pattern === url)
}
/* eslint-enable no-multi-spaces */

export const RouteTicketingSystemsByPartner = ({ partnerId }) => {
  return RouteTicketingSystems() + '?' + stringifyQuery({ partnerId })
}
